import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "../authenticatedFetch";
import type { PublicWidgetConfig, WorkflowDefinition } from "@/stores/workflowsStore";

// Request payload for publishing a widget.
// Note: recipientAddress, chainId, tokenAddress are derived from trigger on backend.
export interface PublishWidgetRequest {
  title: string;
  description?: string;
  buttonLabel?: string;
  minAmount?: string;
  maxAmount?: string;
}

// Response from the public widget endpoint
export interface PublicWidgetResponse {
  workflowId: string;
  workflowName: string;
  widget: PublicWidgetConfig;
}

interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/**
 * Fetch public widget configuration for a workflow.
 * This is a public endpoint that does not require authentication.
 */
export async function fetchPublicWidget(workflowId: string): Promise<PublicWidgetResponse> {
  const response = await fetch(`${V2_ENDPOINT}/v1/public/workflows/${workflowId}/widget`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Public widget not found or not enabled for this workflow");
    }
    throw new Error(`Failed to fetch public widget: ${response.statusText}`);
  }

  const data: ApiResponse<PublicWidgetResponse> = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}

/**
 * Enable and configure a public widget for a workflow.
 * Requires authentication.
 */
export async function publishWidget(workflowId: string, config: PublishWidgetRequest): Promise<PublicWidgetConfig> {
  const response = await authenticatedFetch<ApiResponse<PublicWidgetConfig>>(
    `${V2_ENDPOINT}/v1/workflows/${workflowId}/widget`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(config),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to publish widget: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}

/**
 * Disable the public widget for a workflow.
 * Requires authentication.
 */
export async function unpublishWidget(workflowId: string): Promise<void> {
  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${workflowId}/widget`, {
    method: "DELETE",
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to unpublish widget: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }
}

// Simplified node state for public view
export interface PublicNodeState {
  type: string;
  status: "pending" | "running" | "success" | "failure" | "skipped";
  startedAt?: string;
  finishedAt?: string;
}

// Simplified workflow node for public view
export interface PublicWorkflowNode {
  type: string;
  children?: string[];
}

// Response from the public run by txHash endpoint
export interface PublicRunByTxResponse {
  runId: string;
  workflowId: string;
  status: "running" | "success" | "failure";
  startedAt?: string;
  finishedAt?: string;
  // Step-by-step execution data
  nodes?: Record<string, PublicWorkflowNode>;
  executionState?: Record<string, PublicNodeState>;
}

/**
 * Find a run by its trigger transaction hash.
 * This is a public endpoint that does not require authentication.
 * Returns null if not found (run may not be created yet).
 */
export async function fetchPublicRunByTxHash(txHash: string): Promise<PublicRunByTxResponse | null> {
  const response = await fetch(`${V2_ENDPOINT}/v1/public/runs/by-tx/${txHash}`);

  if (!response.ok) {
    if (response.status === 404) {
      return null; // Run not found yet
    }
    throw new Error(`Failed to fetch run by txHash: ${response.statusText}`);
  }

  const data: ApiResponse<PublicRunByTxResponse> = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}

// ── Visibility-based public endpoints ──────────────────────────────

// Public workflow data from visibility-based endpoint
export interface PublicWorkflowData {
  id: string;
  version: number;
  name: string;
  description: string;
  type: string;
  status: string;
  visibility: "public_view" | "public_execute";
  definition: WorkflowDefinition;
  createdAt: string;
}

// Public run list item (slim)
export interface PublicRunListItem {
  id: string;
  workflowId: string;
  status: string;
  startedAt?: string;
  finishedAt?: string;
}

interface PaginatedResponse<T> {
  items: T[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

export const publicWorkflowQueryKey = (workflowId: string, version?: number) =>
  ["publicWorkflow", workflowId, version ?? null] as const;

/**
 * Fetch a public workflow by visibility (public_view or public_execute).
 * No auth required. When version is provided, fetches that exact version.
 */
export async function fetchPublicWorkflow(workflowId: string, version?: number): Promise<PublicWorkflowData> {
  const url = new URL(`${V2_ENDPOINT}/v1/public/workflows/${workflowId}`, window.location.origin);
  if (version) {
    url.searchParams.set("v", String(version));
  }
  const response = await fetch(url.toString());

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Workflow not found or not publicly accessible");
    }
    throw new Error(`Failed to fetch public workflow: ${response.statusText}`);
  }

  const data: ApiResponse<PublicWorkflowData> = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}

/**
 * Trigger a public workflow run (only for public_execute workflows).
 * No auth required. Returns the run ID.
 */
export async function triggerPublicRun(workflowId: string, input?: Record<string, unknown>): Promise<string> {
  const response = await fetch(`${V2_ENDPOINT}/v1/public/workflows/${workflowId}/run`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input ?? {}),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("This workflow does not allow public execution");
    }
    if (response.status === 404) {
      throw new Error("Workflow not found or not publicly accessible");
    }
    throw new Error(`Failed to trigger run: ${response.statusText}`);
  }

  const data: ApiResponse<{ runId: string }> = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data.runId;
}

/**
 * Fetch paginated run list for a public workflow.
 * No auth required.
 */
export async function fetchPublicWorkflowRuns(
  workflowId: string,
  limit = 20,
  offset = 0,
): Promise<PaginatedResponse<PublicRunListItem>> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const response = await fetch(`${V2_ENDPOINT}/v1/public/workflows/${workflowId}/runs?${params}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Workflow not found or not publicly accessible");
    }
    throw new Error(`Failed to fetch runs: ${response.statusText}`);
  }

  const data: ApiResponse<PaginatedResponse<PublicRunListItem>> = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}

/**
 * Fetch a single public run by ID.
 * No auth required.
 */
export async function fetchPublicRun(runId: string): Promise<PublicRunByTxResponse> {
  const response = await fetch(`${V2_ENDPOINT}/v1/public/runs/${runId}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Run not found or workflow not publicly accessible");
    }
    throw new Error(`Failed to fetch public run: ${response.statusText}`);
  }

  const data: ApiResponse<PublicRunByTxResponse> = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}
